import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fetchTaskCompletions } from '../v1-client';
import type { PaginatedTaskCompletions, SiteId, TaskCompletionRecord } from '../v1';

/** One page of the older view. Small: a phone reads it. */
export const OLDER_TASKS_PAGE_SIZE = 50;

/**
 * Finished tasks from the durable record, a page at a time, newest finish
 * first -- the Tasks tab's "Older tasks".
 *
 * NOTHING IS FETCHED UNTIL SOMEONE ASKS. `startBeforeMs` is null until the
 * person opens the older view, and the query is disabled until then: the
 * live list is already read on every console open and every reconnect,
 * and this must not add a Postgres read to each of them.
 *
 * `startBeforeMs` is part of the key, and the screen FREEZES it when the
 * view opens. A key that followed the live list would throw away every
 * page already loaded whenever the oldest finished task left the live
 * list -- which the retention purge makes happen every few hours. What
 * leaves it while the view is open is read by `useOlderTasksGap` instead.
 * (One exception: a start frozen at "newest" because the live list was
 * empty is taken again once it loads, while nothing loaded survives the
 * de-duplication -- see the screen.)
 *
 * NO AUTOMATIC RE-READS. Refetching an infinite query re-reads every page
 * it holds, one after another; an archive does not change under a person
 * reading it, so focus, reconnect and remount never trigger one, and a
 * failed page is shown once and retried by pressing the button again.
 */
export function useOlderTasks(siteId: string, startBeforeMs: number | null | undefined) {
  return useInfiniteQuery({
    queryKey: [
      'v1',
      siteId,
      'task_completions',
      startBeforeMs === undefined ? 'closed' : (startBeforeMs ?? 'newest'),
    ] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchTaskCompletions(siteId as SiteId, {
        ...(startBeforeMs !== null && startBeforeMs !== undefined
          ? { beforeMs: startBeforeMs }
          : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: OLDER_TASKS_PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: PaginatedTaskCompletions) => last.next_cursor ?? undefined,
    // `undefined` = not opened; `null` = opened, with nothing live to start
    // below (so start from the newest finish).
    enabled: Boolean(siteId) && startBeforeMs !== undefined,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/** The biggest page the server serves; the gap is read in as few as it can. */
const GAP_PAGE_SIZE = 200;
/** A thousand tasks: about sixteen days of NoMad's sixty a day, more than
 *  the whole live window. Past that the read stops and says so rather
 *  than paging on. */
const GAP_MAX_PAGES = 5;

/** One empty list for every render with no gap: the screen memoises the
 *  older rows on these entries, and a fresh `[]` each render would
 *  rebuild them on every live update. */
const NO_ENTRIES: TaskCompletionRecord[] = [];

export interface OlderTasksGap {
  /** Newest finish first, every one at or after the start the older view
   *  opened with. May include tasks the live list still shows; the
   *  screen's de-duplication drops those. */
  entries: TaskCompletionRecord[];
  /** False when the read stopped at `GAP_MAX_PAGES` with more to go. */
  complete: boolean;
}

/**
 * The tasks that left the live list AFTER the older view opened -- read so
 * they land on the older list instead of on neither.
 *
 * WHY THERE IS A GAP. The older view's pages all start below
 * `openedBeforeMs`, the oldest finish the live list held when it opened.
 * The live retention purge (every six hours, on the api-server) deletes
 * the oldest finished tasks first -- exactly the ones just above that
 * start -- and publishes no tombstone, so they drop out of the live list
 * at the console's next re-snapshot. They are above every older page and
 * gone from the live list: an iPad left on Older tasks lost about fifteen
 * jobs from both lists every six hours, with nothing on screen to say so.
 *
 * `liveBeforeMs` is where the older view WOULD start if it opened now
 * (`olderStartBeforeMs` over today's live list), or null when the live
 * list holds no finished task at all. Nothing is read while it is still
 * at or below the start -- the live list has lost nothing above it -- and
 * when it moves, ONE read covers the whole gap, newest first from
 * `liveBeforeMs` down to the first task below `openedBeforeMs`. That is
 * a single request at the purge's rate, not a re-read of the pages the
 * person has already loaded, which stay as they are.
 *
 * Keyed by both bounds, so a later purge widens it with a fresh read; the
 * last answer stays on screen meanwhile so the rows do not blink out.
 */
export function useOlderTasksGap(
  siteId: string,
  openedBeforeMs: number | null | undefined,
  liveBeforeMs: number | null,
) {
  const opened = typeof openedBeforeMs === 'number' ? openedBeforeMs : null;
  const needed = Boolean(siteId) && opened !== null && (liveBeforeMs === null || liveBeforeMs > opened);
  const query = useQuery({
    queryKey: [
      'v1',
      siteId,
      'task_completions',
      'gap',
      opened ?? 'closed',
      liveBeforeMs ?? 'newest',
    ] as const,
    queryFn: async (): Promise<OlderTasksGap> => {
      const entries: TaskCompletionRecord[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < GAP_MAX_PAGES; page++) {
        const res = await fetchTaskCompletions(siteId as SiteId, {
          ...(liveBeforeMs !== null ? { beforeMs: liveBeforeMs } : {}),
          ...(cursor ? { cursor } : {}),
          limit: GAP_PAGE_SIZE,
        });
        for (const record of res.entries) {
          // Below the start: the older view's own pages have it.
          if (record.completed_at_ms < (opened as number)) return { entries, complete: true };
          entries.push(record);
        }
        if (!res.next_cursor) return { entries, complete: true };
        cursor = res.next_cursor;
      }
      return { entries, complete: false };
    },
    enabled: needed,
    // The previous gap's rows stay up while a wider one loads -- but only
    // for the same opening; a reopened view starts from its own start.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[4] === (opened ?? 'closed') ? previous : undefined,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
  return {
    entries: (needed && query.data?.entries) || NO_ENTRIES,
    complete: !needed || query.data?.complete !== false,
    isError: needed && query.isError,
    refetch: query.refetch,
  };
}
